/*
  HAPPE BOX V3 - Thumbnail-Based Combo Preview Controller
  Renders selected customization components as static thumbnails in a horizontal row
  with '+' icons between items, instant updates on state change, and click-to-edit step navigation.
*/

class LivePreviewRenderer {
  constructor() {
    this.canvas = document.getElementById('BuilderPreviewCanvas');
    this.container = document.querySelector('.hp-preview-container') || this.canvas?.parentElement;
    
    this.activeFocusIndex = 0;
    this.lastState = null;

    // Hide old slideshow controls if present
    const controls = document.getElementById('slideshow-controls');
    if (controls) controls.style.display = 'none';

    this.attachEvents();
  }

  attachEvents() {
    document.addEventListener('builder:state-change', (e) => {
      this.lastState = e.detail;
      this.render(e.detail);
    });

    document.addEventListener('builder:step-render', (e) => {
      this.lastState = e.detail.state;
      this.render(e.detail.state, e.detail.currentStepIndex, e.detail.steps);
    });
  }

  getSelectedItems(state) {
    if (!state) return [];
    const items = [];
    const getArr = (val) => Array.isArray(val) ? val : (val ? [val] : []);

    // 1. Base Box
    if (state.base) {
      items.push({
        id: state.base.id,
        title: state.base.title,
        price: state.base.price,
        type: 'base',
        category: 'Box',
        image: state.base.thumbnail || state.base.overlay_image || state.base.image || state.base.featured_image
      });
    }

    // 2. Flowers
    getArr(state.flowers).forEach(item => {
      if (item) {
        items.push({
          id: item.id,
          title: item.title,
          price: item.price,
          type: 'flowers',
          category: 'Flowers',
          image: item.thumbnail || item.overlay_image || item.image || item.featured_image
        });
      }
    });

    // 3. Cake
    if (state.cake) {
      items.push({
        id: state.cake.id,
        title: state.cake.title,
        price: state.cake.price,
        type: 'cake',
        category: 'Cake',
        image: state.cake.thumbnail || state.cake.overlay_image || state.cake.image || state.cake.featured_image
      });
    }

    // 4. Custom Items
    getArr(state.custom_items).forEach(item => {
      if (item) {
        items.push({
          id: item.id,
          title: item.title,
          price: item.price,
          type: 'custom_items',
          category: 'Custom Item',
          image: item.thumbnail || item.overlay_image || item.image || item.featured_image
        });
      }
    });

    // 5. Chocolates
    getArr(state.chocolates).forEach(item => {
      if (item) {
        items.push({
          id: item.id,
          title: item.title,
          price: item.price,
          type: 'chocolate',
          category: 'Chocolate',
          image: item.thumbnail || item.overlay_image || item.image || item.featured_image
        });
      }
    });

    // 6. Balloons
    getArr(state.balloons).forEach(item => {
      if (item) {
        items.push({
          id: item.id,
          title: item.title,
          price: item.price,
          type: 'balloon',
          category: 'Balloon',
          image: item.thumbnail || item.overlay_image || item.image || item.featured_image
        });
      }
    });

    // 7. Extras
    getArr(state.extras).forEach(item => {
      if (item) {
        items.push({
          id: item.id,
          title: item.title,
          price: item.price,
          type: 'extras',
          category: 'Add-on',
          image: item.thumbnail || item.overlay_image || item.image || item.featured_image
        });
      }
    });

    return items;
  }

  render(state, currentStepIndex = 0, steps = []) {
    const items = this.getSelectedItems(state);

    if (this.activeFocusIndex >= items.length) {
      this.activeFocusIndex = Math.max(0, items.length - 1);
    }

    this.renderThumbnailPanel(items);
  }

  renderThumbnailPanel(items) {
    if (!this.container) {
      this.container = document.querySelector('.hp-preview-container') || document.getElementById('BuilderPreviewCanvas')?.parentElement;
      if (!this.container) return;
    }

    let panel = this.container.querySelector('.hp-thumb-preview-panel');
    if (!panel) {
      this.container.innerHTML = `<div class="hp-thumb-preview-panel"></div>`;
      panel = this.container.querySelector('.hp-thumb-preview-panel');
    }

    if (!items || items.length === 0) {
      panel.innerHTML = `
        <div class="hp-preview-empty-card">
          <div class="hp-preview-empty-icon">🎁</div>
          <h4 class="hp-preview-empty-title">Your Custom Gift Combo</h4>
          <p class="hp-preview-empty-sub">Select items from the left to start building your customized box</p>
        </div>
      `;
      return;
    }

    const focusedItem = items[this.activeFocusIndex] || items[0];
    const fallbackImg = 'https://images.unsplash.com/photo-1549463140-3101fd58060b?q=80&w=240';

    let html = `
      <div class="hp-preview-focus-card">
        <div class="hp-focus-img-wrap">
          <img src="${focusedItem.image || fallbackImg}" alt="${focusedItem.title}" class="hp-focus-img" onerror="this.onerror=null; this.src='${fallbackImg}';">
        </div>
        <div class="hp-focus-info">
          <span class="hp-focus-category">${focusedItem.category}</span>
          <span class="hp-focus-title">${focusedItem.title}</span>
        </div>
      </div>

      <div class="hp-chain-section">
        <div class="hp-chain-header">
          <span class="hp-chain-label">COMBINED GIFT (${items.length} ${items.length === 1 ? 'ITEM' : 'ITEMS'})</span>
          <span class="hp-chain-hint">Click thumbnail to edit</span>
        </div>
        <div class="hp-chain-row">
    `;

    items.forEach((item, index) => {
      const isFocused = index === this.activeFocusIndex;
      const thumbUrl = item.image || fallbackImg;
      html += `
        <div class="hp-chain-thumb-card ${isFocused ? 'is-active' : ''}" 
             data-index="${index}" 
             data-step-type="${item.type}"
             title="Edit ${item.category}: ${item.title}">
          <div class="hp-thumb-img-box">
            <img src="${thumbUrl}" alt="${item.title}" onerror="this.onerror=null; this.src='${fallbackImg}';">
          </div>
          <span class="hp-thumb-category-tag">${item.category}</span>
        </div>
      `;

      if (index < items.length - 1) {
        html += `<div class="hp-chain-plus-badge">+</div>`;
      }
    });

    html += `
        </div>
      </div>
    `;

    panel.innerHTML = html;

    panel.querySelectorAll('.hp-chain-thumb-card').forEach(card => {
      card.addEventListener('click', () => {
        const idx = parseInt(card.getAttribute('data-index'), 10);
        const stepType = card.getAttribute('data-step-type');
        
        this.activeFocusIndex = idx;

        if (window.GiftBuilderUI && typeof window.GiftBuilderUI.goToStepType === 'function') {
          window.GiftBuilderUI.goToStepType(stepType);
        }
        this.renderThumbnailPanel(items);
      });
    });
  }

  showImage(imageSrc) {
    if (!this.lastState) return;
    const items = this.getSelectedItems(this.lastState);
    const foundIdx = items.findIndex(i => i.image === imageSrc);
    if (foundIdx !== -1) {
      this.activeFocusIndex = foundIdx;
      this.renderThumbnailPanel(items);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.PreviewRenderer = new LivePreviewRenderer();
});
